// features/common/CameraModal.js
// Full-screen camera component using getUserMedia() with real-time geo-metadata overlay
// Designed for iPad field workers - captures photos with GPS, compass, elevation, and address

import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useGeolocation } from "../../hooks/useGeolocation";
import { useCompass } from "../../hooks/useCompass";
import { reverseGeocode } from "../../api/AddressApi";
import {
  formatBearing,
  formatCoordinates,
  formatElevation,
  formatAccuracy,
} from "../../utils/coordinateUtils";
import styles from "./CameraModal.module.css";

// Haversine distance in meters between two lat/lng points
const haversineDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/**
 * CameraModal - Full-screen camera with geo-metadata overlay
 *
 * @param {object} props
 * @param {boolean} props.isOpen - Whether the camera modal is open
 * @param {function} props.onCapture - Called with { blob, geoMetadata } when photo confirmed
 * @param {function} props.onClose - Called when camera is dismissed
 */
export const CameraModal = ({ isOpen, onCapture, onClose }) => {
  // Camera state
  const [stream, setStream] = useState(null);
  const [capturedBlob, setCapturedBlob] = useState(null);
  const [capturedUrl, setCapturedUrl] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const [showCompassPrompt, setShowCompassPrompt] = useState(false);
  const [compassPromptDismissed, setCompassPromptDismissed] = useState(false);

  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Geo hooks
  const position = useGeolocation(isOpen);
  const compass = useCompass(isOpen);

  // Location warning state (iOS Chrome specific)
  const [locationWarningDismissed, setLocationWarningDismissed] = useState(false);

  // Address state (reverse geocoded with debounce)
  const [address, setAddress] = useState(null);
  const lastGeocodePosRef = useRef({ lat: null, lng: null, time: 0 });

  // Snapshot of geo data at capture time
  const capturedGeoRef = useRef(null);

  // Derive location warning state from geolocation errors
  const showLocationWarning =
    !locationWarningDismissed &&
    position.errorCode != null &&
    position.latitude == null;

  const locationWarningMessage =
    position.isIOSChrome && position.errorCode === "PERMISSION_DENIED"
      ? "Location unavailable in this browser. Go to iPad Settings > Privacy & Security > Location Services > Chrome and set to \"While Using the App\"."
      : position.errorCode === "PERMISSION_DENIED"
        ? "Location permission denied. Please allow location access for this site in your browser settings."
        : position.errorCode === "TIMEOUT"
          ? "Location request timed out. GPS signal may be weak in this area. Photos will be saved without location data."
          : position.errorCode === "POSITION_UNAVAILABLE"
            ? "Location unavailable. GPS signal may be blocked. Photos will be saved without location data."
            : null;

  // Start camera stream
  const startCamera = useCallback(async (facing) => {
    try {
      setCameraError(null);
      const constraints = {
        video: {
          facingMode: facing,
          width: { ideal: 3264 },
          height: { ideal: 2448 },
        },
        audio: false,
      };
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      setCameraError(err.message || "Failed to access camera");
    }
  }, []);

  // Stop camera stream
  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [stream]);

  // Open/close lifecycle
  useEffect(() => {
    if (isOpen) {
      startCamera(facingMode);
    }
    return () => {
      // Always clean up stream on unmount or close
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Show compass permission prompt on iOS if needed
  useEffect(() => {
    if (isOpen && compass.permissionState === "prompt" && !compassPromptDismissed) {
      setShowCompassPrompt(true);
    }
  }, [isOpen, compass.permissionState, compassPromptDismissed]);

  // Debounced reverse geocoding (~50m movement or 30s elapsed)
  useEffect(() => {
    if (!isOpen || position.latitude == null || position.longitude == null) return;

    const last = lastGeocodePosRef.current;
    const now = Date.now();
    const timeSince = now - last.time;

    // Check if we need to re-geocode
    let shouldGeocode = false;
    if (!last.lat || !last.lng) {
      shouldGeocode = true;
    } else {
      const dist = haversineDistance(last.lat, last.lng, position.latitude, position.longitude);
      if (dist > 50 || timeSince > 30000) {
        shouldGeocode = true;
      }
    }

    if (!shouldGeocode) return;

    lastGeocodePosRef.current = {
      lat: position.latitude,
      lng: position.longitude,
      time: now,
    };

    reverseGeocode(position.latitude, position.longitude).then((addr) => {
      if (addr) setAddress(addr);
    });
  }, [isOpen, position.latitude, position.longitude]);

  // Handle compass permission request (iOS)
  const handleCompassPermission = async () => {
    await compass.requestPermission();
    setShowCompassPrompt(false);
  };

  const handleSkipCompass = () => {
    setShowCompassPrompt(false);
    setCompassPromptDismissed(true);
  };

  // Capture photo
  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Adjust bearing for front camera (faces opposite direction from compass heading)
    let captureBearing = compass.bearing;
    if (captureBearing != null && facingMode === "user") {
      captureBearing = (captureBearing + 180) % 360;
    }

    const now = new Date();

    // Snapshot geo data at capture moment
    capturedGeoRef.current = {
      latitude: position.latitude,
      longitude: position.longitude,
      altitude: position.altitude,
      accuracy: position.accuracy,
      altitudeAccuracy: position.altitudeAccuracy,
      bearing: captureBearing,
      address: address,
      capturedAt: now.toISOString(),
      capturedAtLocal: now.toLocaleString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }),
    };

    canvas.toBlob(
      (blob) => {
        if (blob) {
          setCapturedBlob(blob);
          setCapturedUrl(URL.createObjectURL(blob));
          // Pause the video stream (keep tracks alive for retake)
          video.pause();
        }
      },
      "image/jpeg",
      0.92,
    );
  };

  // Retake photo
  const handleRetake = () => {
    if (capturedUrl) {
      URL.revokeObjectURL(capturedUrl);
    }
    setCapturedBlob(null);
    setCapturedUrl(null);
    capturedGeoRef.current = null;

    if (videoRef.current && stream) {
      videoRef.current.play();
    }
  };

  // Confirm and use photo
  const handleUsePhoto = () => {
    if (capturedBlob && onCapture) {
      onCapture({
        blob: capturedBlob,
        geoMetadata: capturedGeoRef.current,
      });
    }
    handleClose();
  };

  // Close camera
  const handleClose = () => {
    stopCamera();
    if (capturedUrl) {
      URL.revokeObjectURL(capturedUrl);
    }
    setCapturedBlob(null);
    setCapturedUrl(null);
    setCameraError(null);
    setAddress(null);
    capturedGeoRef.current = null;
    lastGeocodePosRef.current = { lat: null, lng: null, time: 0 };
    setShowCompassPrompt(false);
    setCompassPromptDismissed(false);
    setLocationWarningDismissed(false);
    onClose();
  };

  // Flip camera
  const handleFlipCamera = () => {
    stopCamera();
    const newFacing = facingMode === "environment" ? "user" : "environment";
    setFacingMode(newFacing);
    startCamera(newFacing);
  };

  if (!isOpen) return null;

  const isPreviewMode = capturedBlob !== null;
  const bearingStr = formatBearing(compass.bearing);
  const coordsStr = formatCoordinates(position.latitude, position.longitude);
  const elevStr = formatElevation(position.altitude);
  const accStr = formatAccuracy(position.accuracy);

  return createPortal(
    <div className={styles.cameraOverlay}>
      {/* iOS Compass Permission Prompt */}
      {showCompassPrompt && (
        <div className={styles.compassPrompt}>
          <p className={styles.compassPromptText}>
            Enable compass to capture bearing data with your photos.
          </p>
          <button
            className={styles.compassPromptButton}
            onClick={handleCompassPermission}
            type="button"
          >
            Enable Compass
          </button>
          <br />
          <button
            className={styles.compassPromptSkip}
            onClick={handleSkipCompass}
            type="button"
          >
            Skip
          </button>
        </div>
      )}

      {/* Location Warning Banner (iOS Chrome / permission denied) */}
      {showLocationWarning && locationWarningMessage && (
        <div className={styles.locationWarning}>
          <p className={styles.locationWarningText}>
            {locationWarningMessage}
          </p>
          <button
            className={styles.locationWarningDismiss}
            onClick={() => setLocationWarningDismissed(true)}
            type="button"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Camera View */}
      <div className={styles.cameraView}>
        {/* Metadata Ribbons */}
        {!cameraError && (
          <div className={styles.ribbonContainer}>
            <div className={styles.ribbon}>
              <span className={styles.ribbonText}>
                {address || "Acquiring address..."}
              </span>
            </div>
            <div className={styles.ribbon}>
              {bearingStr && (
                <>
                  <span>{bearingStr}</span>
                  <span className={styles.ribbonSeparator}>|</span>
                </>
              )}
              <span>{coordsStr || "Acquiring GPS..."}</span>
              {elevStr && (
                <>
                  <span className={styles.ribbonSeparator}>|</span>
                  <span>{elevStr}</span>
                </>
              )}
              {accStr && (
                <>
                  <span className={styles.ribbonSeparator}>|</span>
                  <span>{accStr}</span>
                </>
              )}
            </div>
          </div>
        )}

        {/* Video feed or captured preview */}
        {cameraError ? (
          <div className={styles.errorOverlay}>
            <p className={styles.errorText}>{cameraError}</p>
            <button
              className={styles.controlButton}
              onClick={() => startCamera(facingMode)}
              type="button"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              className={styles.video}
              autoPlay
              playsInline
              muted
              style={isPreviewMode ? { display: "none" } : undefined}
            />
            {isPreviewMode && (
              <img
                src={capturedUrl}
                alt="Captured preview"
                className={styles.capturedPreview}
              />
            )}
          </>
        )}
      </div>

      {/* Controls Bar */}
      <div className={styles.controlsBar}>
        {isPreviewMode ? (
          <div className={styles.previewControls}>
            <button
              className={styles.retakeButton}
              onClick={handleRetake}
              type="button"
            >
              Retake
            </button>
            <button
              className={styles.usePhotoButton}
              onClick={handleUsePhoto}
              type="button"
            >
              Use Photo
            </button>
          </div>
        ) : (
          <>
            {/* Cancel */}
            <button
              className={styles.controlButton}
              onClick={handleClose}
              type="button"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
              Cancel
            </button>

            {/* Capture */}
            <button
              className={styles.captureButton}
              onClick={handleCapture}
              disabled={!stream || cameraError}
              type="button"
              aria-label="Capture photo"
            >
              <div className={styles.captureButtonInner} />
            </button>

            {/* Flip Camera */}
            <button
              className={styles.controlButton}
              onClick={handleFlipCamera}
              type="button"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M11 19H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" />
                <path d="M13 5h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-5" />
                <polyline points="16 3 19 6 16 9" />
                <polyline points="8 15 5 18 8 21" />
              </svg>
              Flip
            </button>
          </>
        )}
      </div>

      {/* Hidden canvas for capture */}
      <canvas ref={canvasRef} className={styles.hiddenCanvas} />
    </div>,
    document.body,
  );
};

export default CameraModal;
